// Zyla feed alias tables — short-form → SKOHR canonical names.
//
// The Zyla NRL fixture/match endpoints emit team and venue SHORT FORMS
// (e.g. "Brisbane", "Melbourne", "Suncorp", "CommBank"). Our schema and the
// rest of the app use the canonical names seeded in `supabase/seed.sql`
// (17 clubs; the full venue set). This module is the single translation
// surface: a short form that is not known THROWS rather than silently
// producing a fixture row with a bogus team/venue (FEED-3 fail-loud rule).
//
// Pure, dependency-free TypeScript. No vendor hostname lives here (SEC-5).

// ── teams ────────────────────────────────────────────────────────────────
// Canonical names match the 17 clubs seeded in supabase/seed.sql exactly.
const TEAM_ALIASES: Readonly<Record<string, string>> = {
  // short forms appearing in the 2026 recorded samples
  Brisbane: "Brisbane Broncos",
  Melbourne: "Melbourne Storm",
  Manly: "Manly-Warringah Sea Eagles",
  "St Geo Illa": "St. George Illawarra Dragons",
  Penrith: "Penrith Panthers",
  Canterbury: "Canterbury-Bankstown Bulldogs",
  "Gold Coast": "Gold Coast Titans",
  "South Sydney": "South Sydney Rabbitohs",
  Sydney: "Sydney Roosters",
  Dolphins: "Dolphins",
  "North Qld": "North Queensland Cowboys",
  "Wests Tigers": "Wests Tigers",
  Warriors: "New Zealand Warriors",
  Newcastle: "Newcastle Knights",
  Parramatta: "Parramatta Eels",
  Cronulla: "Cronulla-Sutherland Sharks",
  // obvious variants (broader coverage; an unknown alias must still fail loud)
  Canberra: "Canberra Raiders",
  Souths: "South Sydney Rabbitohs",
  "St George Illawarra": "St. George Illawarra Dragons",
  Dragons: "St. George Illawarra Dragons",
  "St George": "St. George Illawarra Dragons",
  Roosters: "Sydney Roosters",
  Bulldogs: "Canterbury-Bankstown Bulldogs",
  Sharks: "Cronulla-Sutherland Sharks",
  Titans: "Gold Coast Titans",
  Eels: "Parramatta Eels",
  Knights: "Newcastle Knights",
  Panthers: "Penrith Panthers",
  Storm: "Melbourne Storm",
  Broncos: "Brisbane Broncos",
  "Sea Eagles": "Manly-Warringah Sea Eagles",
  Cowboys: "North Queensland Cowboys",
  "Nth Qld": "North Queensland Cowboys",
  "NQ Cowboys": "North Queensland Cowboys",
  "NZ Warriors": "New Zealand Warriors",
  Wests: "Wests Tigers",
  Redcliffe: "Dolphins",
  Raiders: "Canberra Raiders",
};

// ── venues ───────────────────────────────────────────────────────────────
// Canonical names match the venues seeded in supabase/seed.sql exactly.
// Round 6 wave A added the recorded-provider (RLP) short forms for the seeded
// venues it uses (Allegiant, McD. Jones, Optus Stadium, SGV Jubilee, HBF) —
// the existing entries still resolve to the same canonical names.
const VENUE_ALIASES: Readonly<Record<string, string>> = {
  // short forms appearing in the 2026 recorded samples
  Suncorp: "Suncorp Stadium",
  // RLP's short name for the 2026 semi-final ground
  "Eden Park": "Eden Park, Auckland",
  "4 Pines": "4 Pines Park",
  CommBank: "CommBank Stadium",
  "Cbus Super": "Cbus Super Stadium",
  Allianz: "Allianz Stadium",
  "QLD C. Bank": "Queensland Country Bank Stadium",
  "Go Media": "Go Media Stadium",
  // obvious variants for the rest of the seeded venue set
  AAMI: "AAMI Park",
  Accor: "Accor Stadium",
  BlueBet: "BlueBet Stadium",
  Campbelltown: "Campbelltown Sports Stadium",
  GIO: "GIO Stadium",
  "Glen Willow": "Glen Willow, Mudgee",
  "HBF Park": "HBF Park, Perth",
  Kayo: "Kayo Stadium",
  Leichhardt: "Leichhardt Oval",
  "McDonald Jones": "McDonald Jones Stadium",
  MCG: "Melbourne Cricket Ground",
  "Netstrata Jubilee": "Netstrata Jubilee Stadium",
  Jubilee: "Netstrata Jubilee Stadium",
  "One NZ": "One NZ Stadium, Christchurch",
  Optus: "Optus Stadium, Perth",
  PointsBet: "PointsBet Stadium",
  "SKY Stadium": "SKY Stadium, Wellington",
  TIO: "TIO Stadium, Darwin",
  WIN: "WIN Stadium",
  // recorded-provider (RLP) short forms naming seeded venues (round 6 wave A)
  Allegiant: "Allegiant Stadium, Las Vegas",
  "McD. Jones": "McDonald Jones Stadium",
  "Optus Stadium": "Optus Stadium, Perth",
  "SGV Jubilee": "Netstrata Jubilee Stadium",
  HBF: "HBF Park, Perth",
  // RLP's current short names for seeded grounds (hotfix 15h)
  "Ocean Protect": "PointsBet Stadium",
  Carrington: "Carrington Park, Bathurst",
  polytec: "Central Coast Stadium, Gosford",
  Hnry: "SKY Stadium, Wellington",
};

/**
 * Resolve a Zyla team short form to our canonical club name. Throws on any
 * unknown short form — an unknown alias must NEVER silently create a fixture.
 */
export function resolveTeam(short: string): string {
  const resolved = TEAM_ALIASES[short];
  if (!resolved) {
    throw new Error(`resolveTeam: unknown team alias "${short}"`);
  }
  return resolved;
}

/**
 * Resolve a Zyla venue short form to our canonical venue name. Throws on any
 * unknown short form.
 */
export function resolveVenue(short: string): string {
  const resolved = VENUE_ALIASES[short];
  if (!resolved) {
    throw new Error(`resolveVenue: unknown venue alias "${short}"`);
  }
  return resolved;
}

export interface ZylaFixtureRow {
  match_id: string;
  date: string;
  time: string;
  home_team: string;
  away_team: string;
  venue: string;
  referee?: string | null;
  attendance?: string | null;
  home_score?: string | number | null;
  away_score?: string | number | null;
  match_status?: string | null;
}

export interface UnknownAliasesReport {
  teams: string[];
  venues: string[];
}

/**
 * Scan a set of fixture rows and report any team/venue short forms that are
 * NOT covered by the alias tables. Returns deduped, sorted arrays — empty when
 * every alias resolves. Used by admin/tooling to catch a new short form before
 * it reaches `parseFixtureRow` (which would throw).
 */
export function unknownAliases(rows: ZylaFixtureRow[]): UnknownAliasesReport {
  const teams = new Set<string>();
  const venues = new Set<string>();
  for (const row of rows) {
    if (row.home_team && !TEAM_ALIASES[row.home_team]) teams.add(row.home_team);
    if (row.away_team && !TEAM_ALIASES[row.away_team]) teams.add(row.away_team);
    if (row.venue && !VENUE_ALIASES[row.venue]) venues.add(row.venue);
  }
  return {
    teams: [...teams].sort(),
    venues: [...venues].sort(),
  };
}

/**
 * The canonical team names the alias table resolves to (the 17 seeded clubs).
 * Exposed for tests/contracts; not used by the parsers directly.
 */
export const CANONICAL_TEAMS: readonly string[] = Object.values(TEAM_ALIASES)
  .filter((name, index, all) => all.indexOf(name) === index)
  .sort();
